import { Button, Container, Heading, Text, useBreakpointValue, VStack } from '@chakra-ui/react';
import { useAtomValue } from 'jotai';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchInvitationalLetterDetails } from '../api/Invitations';
import { LetterInviteParameters, routes } from '../data/routes';
import { sessionAtom } from '../lib/Session';
import ShortUuids from '../lib/ShortUuids';
import { Fade } from './Fade';
import { Fetching } from './Fetching';
import { useInfoToast } from './hooks/toasters';
import { useInvitations } from './hooks/useInvitations';
import LetterView from './LetterView';

export type InvitationalLetterDetails = {
  first_name: string;
  last_name: string;
  temp_contact_name: string;
  title: string;
  body: string;
  sent_at: string;
  redeemed: boolean;
};

const InvitationAction = ({ id, inviter }: { id: string; inviter: string }) => {
  const session = useAtomValue(sessionAtom);

  const toaster = useInfoToast();
  const navigate = useNavigate();

  const { dispatch } = useInvitations();

  const onClick = () => {
    dispatch({ type: 'setInvitation', invitationId: id });

    if (session) return;

    toaster({
      title: 'Please log in or register',
      description: `to accept ${inviter}'s invitation`,
    });
    navigate(routes.register);
  };

  return <Button onClick={onClick}>Accept invitation</Button>;
};

const LetterInvite = () => {
  const { invitationId } = useParams<LetterInviteParameters>() as LetterInviteParameters;
  const responsivePadding = useBreakpointValue({ base: 4, xs: 8, lg: 16 });
  const invitationIdUuid = ShortUuids.decode(invitationId);

  const { data, isLoading } = useQuery(
    ['invitational-letter', invitationIdUuid],
    fetchInvitationalLetterDetails(invitationIdUuid)
  );

  return (
    <Fade>
      <Container maxW="container.lg" p={responsivePadding} pt={0}>
        <VStack w="100%" alignItems="flex-start" spacing={8}>
          <VStack w="100%" alignItems="flex-start" spacing={[2, 2, 4]}>
            <Fetching isFetching={isLoading}>
              {data && (
                <>
                  <Heading size="md">
                    Hey {data.temp_contact_name}, {data.first_name} {data.last_name} has written you
                    a letter.
                  </Heading>
                  <LetterView letter={{ title: data.title, body: data.body }} />

                  <Text>
                    {data.first_name} has spent their time crafting this letter for you. Letter
                    writing can become a valuable and enriching form of communication. If you wish
                    to reply and <strong>continue your correspondence</strong> accept this
                    invitation.
                  </Text>
                  <InvitationAction id={invitationIdUuid} inviter={data.first_name} />
                </>
              )}
            </Fetching>
          </VStack>
        </VStack>
      </Container>
    </Fade>
  );
};

export default LetterInvite;
